/**
 * InMail Library › writing rules and the sample draft (Sofia, Oct 19 2026).
 *
 * - Fixed rules: what no text here can change (read-only; enforced by the draft checks).
 * - Standard subject: team-wide, versioned; used whenever the template has no subject (Oct 26 2026).
 * - Team writing rules: one text for everyone with access, versioned — a save from a stale version
 *   is refused, older versions can be read and restored. Until someone saves, Maxi's defaults.
 * - My writing rules: per recruiter; where they disagree with the team rules, mine win.
 * - Sample draft: a role, a person, and the draft the agent would write with the rules exactly as
 *   they stand in the editor. Nothing is saved.
 */
import { useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { useAuth } from '../../../../contexts/AuthContext';
import { useGmailToken } from '../taActionBoard/useGmailToken';
import * as api from '../../services/taOutreachApi';
import type { Campaign, OutreachCandidate, Playbook } from '../../types/taOutreach';
import { LinkedText } from './InlineLinkText';
import { SUBJECT_OVERWRITE_WARNING } from './subjectWarning';

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }) : '';
const errText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

function Section({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2 rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
      <h2 className="m-0 text-sm font-semibold text-gray-900">{title}</h2>
      <p className="m-0 text-xs text-gray-500">{hint}</p>
      {children}
    </section>
  );
}

export function InMailRulesPanel() {
  const [fixed, setFixed] = useState<string[]>([]);
  const [team, setTeam] = useState<Playbook | null>(null);
  const [teamText, setTeamText] = useState('');
  const [mine, setMine] = useState('');
  const [mineText, setMineText] = useState('');
  const [subject, setSubject] = useState<api.DefaultSubject | null>(null);
  const [subjectDe, setSubjectDe] = useState('');
  const [subjectEn, setSubjectEn] = useState('');
  const [busy, setBusy] = useState<'team' | 'mine' | 'subject' | null>(null);
  const [history, setHistory] = useState<api.PlaybookVersion[] | null>(null);

  useEffect(() => {
    let alive = true;
    void api
      .fetchLibraryRules()
      .then((r) => {
        if (!alive) return;
        setFixed(r.fixed);
        setTeam(r.team);
        setTeamText(r.team.markdown);
        setMine(r.mine);
        setMineText(r.mine);
        if (r.subject) {
          setSubject(r.subject);
          setSubjectDe(r.subject.de);
          setSubjectEn(r.subject.en);
        }
      })
      .catch((err) => showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not load the writing rules.') }));
    return () => {
      alive = false;
    };
  }, []);

  async function saveTeam() {
    if (!team) return;
    setBusy('team');
    try {
      const { playbook } = await api.savePlaybook('outreach', teamText, team.version);
      setTeam(playbook);
      setTeamText(playbook.markdown);
      setHistory(null);
      showToast({ status: 'success', description: `Saved as version ${playbook.version}. New drafts use it.` });
      void logAnalyticsEvent('ta_outreach_writing_rules_saved', { which: 'team' });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not save.') });
    } finally {
      setBusy(null);
    }
  }

  async function saveSubject() {
    if (!subject) return;
    if (!window.confirm(SUBJECT_OVERWRITE_WARNING)) return;
    setBusy('subject');
    try {
      const saved = (await api.saveDefaultSubject(subjectDe, subjectEn, subject.version)).subject;
      setSubject(saved);
      setSubjectDe(saved.de);
      setSubjectEn(saved.en);
      showToast({ status: 'success', description: 'Saved. New drafts use it.' });
      void logAnalyticsEvent('ta_outreach_default_subject_saved', { empty: !saved.de && !saved.en });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not save.') });
    } finally {
      setBusy(null);
    }
  }

  async function saveMine() {
    setBusy('mine');
    try {
      const { style } = await api.saveStyle({ writingRules: mineText });
      setMine(style.writingRules || '');
      setMineText(style.writingRules || '');
      showToast({ status: 'success', description: 'Saved. Your new drafts use it.' });
      void logAnalyticsEvent('ta_outreach_writing_rules_saved', { which: 'mine' });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not save.') });
    } finally {
      setBusy(null);
    }
  }

  async function openHistory() {
    try {
      setHistory((await api.fetchPlaybookHistory('outreach')).history);
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not load the history.') });
    }
  }

  if (!team) return null;
  return (
    <div className="mb-5 space-y-4">
      <Section title="Fixed rules" hint="Every InMail follows these. No writing rule below can change them — a draft that breaks one is not shown.">
        <ul className="m-0 list-disc space-y-1 pl-5 text-[13px] text-gray-700">
          {fixed.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </Section>

      <p className="m-0 rounded-md border border-solid border-[#DBEBFC] bg-[#F3F8FE] px-4 py-3 text-[13px] text-[#1A1A1A]">
        <b>What writing rules change:</b> only the parts the agent writes itself — the personal sentence about the profile (in every
        InMail), the role paragraph and the sentence on where the role is. Everything else is the template: change it there. Which
        template a role uses, the language and the follow-up days are set in the role’s step 1.
      </p>

      {subject && (
        <Section
          title="Standard subject"
          hint={`Used when the InMail's template has no subject of its own; a template with a subject keeps it. {candidateFirstName} and {roleTitle} are filled in. ${subject.version ? `Version ${subject.version}, saved by ${subject.updatedBy || 'someone'} on ${when(subject.updatedAt)}.` : 'The default — nobody has saved one yet.'}`}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Input label="Subject (DE)" value={subjectDe} maxLength={120} disabled={busy === 'subject'} onChange={(e) => setSubjectDe((e.target as HTMLInputElement).value)} />
            <Input label="Subject (EN)" value={subjectEn} maxLength={120} disabled={busy === 'subject'} onChange={(e) => setSubjectEn((e.target as HTMLInputElement).value)} />
          </div>
          <Button
            variant="primary"
            size="sm"
            className="rounded-md"
            disabled={busy === 'subject' || (subjectDe === subject.de && subjectEn === subject.en)}
            onClick={() => void saveSubject()}
          >
            Save subject
          </Button>
        </Section>
      )}

      <Section
        title="Team writing rules (for the AI parts)"
        hint={`For everyone's InMails: tone and wording of what the agent writes. ${team.version ? `Version ${team.version}, saved by ${team.updatedBy || 'someone'} on ${when(team.updatedAt)}.` : 'The defaults — nobody has saved a version yet.'}`}
      >
        <Textarea label="Team writing rules" rows={6} value={teamText} maxLength={4000} disabled={busy === 'team'} onChange={(e) => setTeamText(e.target.value)} />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" className="rounded-md" disabled={busy === 'team' || teamText === team.markdown || !teamText.trim()} onClick={() => void saveTeam()}>
            Save for the team
          </Button>
          {team.version > 0 && (
            <button type="button" className="text-xs text-[#0072EA] underline" onClick={() => void (history ? setHistory(null) : openHistory())}>
              {history ? 'Hide earlier versions' : 'Earlier versions'}
            </button>
          )}
        </div>
        {history && (
          <ul className="m-0 list-none space-y-2 p-0 text-xs">
            {history.map((h) => (
              <li key={h.version} className="rounded-md border border-solid border-[#E9EAEC] p-2">
                <span className="font-semibold">Version {h.version}</span> · {h.by || 'someone'} · {when(h.at)}{' '}
                <button type="button" className="ml-1 text-[#0072EA] underline" onClick={() => setTeamText(h.markdown)}>
                  Put back in the editor
                </button>
                <pre className="m-0 mt-1 whitespace-pre-wrap font-sans text-gray-600">{h.markdown}</pre>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="My writing rules" hint="Only for your InMails, only you see them. Where they disagree with the team rules, yours win.">
        <Textarea
          label="My writing rules"
          rows={3}
          value={mineText}
          maxLength={2000}
          disabled={busy === 'mine'}
          placeholder="e.g. Keep the pitch to two short sentences. Mention that the team is 8 people."
          onChange={(e) => setMineText(e.target.value)}
        />
        <Button variant="primary" size="sm" className="rounded-md" disabled={busy === 'mine' || mineText === mine} onClick={() => void saveMine()}>
          Save my rules
        </Button>
      </Section>

      {/* "My booking link" is gone (Sofia, Oct 24 2026): the screening link is the only booking link. */}
      <Section
        title="Booking link"
        hint="Your InMails link to your screening link from Interview Links. If you have none yet, the first draft that needs it makes it with the defaults (30 min, Mon–Fri 9–17)."
      >
        <a href="/interview-links?open=screening" className="text-sm font-semibold text-[#0072EA] underline">
          Open Interview Links
        </a>
      </Section>

      <SampleDraft teamRules={teamText} myRules={mineText} />
    </div>
  );
}

const NONE = '__none__';

/** A draft with the rules as they stand in the editors above — saved or not. Nothing is stored. */
function SampleDraft({ teamRules, myRules }: { teamRules: string; myRules: string }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [postingId, setPostingId] = useState('');
  const [people, setPeople] = useState<OutreachCandidate[]>([]);
  const [personId, setPersonId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.draftPreview>> | null>(null);
  /** Set when this sample had to build the role's brief first (Sofia, Oct 19 2026). */
  const [builtBrief, setBuiltBrief] = useState<{ postingId: string; kickoff: boolean } | null>(null);
  // The kick-off sheet is read through the recruiter's Google grant, the same one Outreach uses.
  const { user } = useAuth();
  const gmail = useGmailToken((user?.email || '').toLowerCase());

  useEffect(() => {
    let alive = true;
    void api
      .fetchCampaigns()
      .then((r) => alive && setCampaigns(r.campaigns.filter((c) => c.postingId)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  async function pickRole(id: string) {
    setPostingId(id);
    setPersonId('');
    setResult(null);
    try {
      const { items } = await api.fetchOutreach(id);
      // People with a read profile first: a sample from a list card alone says little.
      const open = items.filter((c) => c.status !== 'dropped');
      setPeople([...open.filter((c) => c.profileText), ...open.filter((c) => !c.profileText)]);
    } catch (err) {
      setPeople([]);
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not load the candidates.') });
    }
  }

  async function write() {
    setBusy(true);
    setResult(null);
    setBuiltBrief(null);
    try {
      let r;
      try {
        r = await api.draftPreview(personId, { teamRules, myRules });
      } catch (err) {
        // No brief for this role yet: build the real one — exactly what step 1 would build, from the
        // Lever posting and the kick-off sheet — and try again. It is kept for the role (one brief per
        // role, never a second sample-only copy); it can be adjusted in step 1.
        if ((err as { body?: { code?: string } })?.body?.code !== 'no_brief') throw err;
        const built = await api.compileBrief(postingId, gmail.token, false);
        setBuiltBrief({ postingId, kickoff: Boolean(built.brief.source?.kickoff) });
        r = await api.draftPreview(personId, { teamRules, myRules });
      }
      setResult(r);
      void logAnalyticsEvent('ta_outreach_sample_draft_written', {});
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: errText(err, 'Could not write a sample draft.') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="Try it: sample draft" hint="Pick a role and a person — the agent writes the InMail with the template and the rules above as they stand now, saved or not. Nothing is saved, nothing is sent.">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={postingId || NONE} onValueChange={(v) => v !== NONE && void pickRole(v)}>
          <SelectTrigger aria-label="Role" className="h-8 w-auto min-w-[220px] text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Pick a role</SelectItem>
            {campaigns.map((c) => (
              <SelectItem key={c.postingId as string} value={c.postingId as string}>
                {c.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={personId || NONE} onValueChange={(v) => setPersonId(v === NONE ? '' : v)} disabled={!people.length}>
          <SelectTrigger aria-label="Person" className="h-8 w-auto min-w-[220px] text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{postingId && !people.length ? 'Nobody on this role yet' : 'Pick a person'}</SelectItem>
            {people.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
                {c.profileText ? '' : ' (list card only)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="primary" size="sm" className="rounded-md" disabled={!personId || busy} onClick={() => void write()}>
          {busy ? 'Writing…' : 'Write sample draft'}
        </Button>
      </div>
      {builtBrief && (
        <p className="m-0 text-xs text-[#707070]">
          This role had no brief yet, so it was built now from {builtBrief.kickoff ? 'the Lever posting and the hiring kick-off sheet' : 'the Lever posting (the kick-off sheet could not be read)'}. It is
          the role’s brief from now on —{' '}
          <a href={`/outreach?posting=${encodeURIComponent(builtBrief.postingId)}`} className="text-[#0072EA] underline">
            check or adjust it in step 1
          </a>
          .
        </p>
      )}
      {result?.refusal && <p className="m-0 text-[13px] text-[#E71D3F]">Not shown — it broke a fixed rule: {result.refusal}</p>}
      {result?.error && <p className="m-0 text-[13px] text-[#E71D3F]">{result.error}</p>}
      {result?.draft && (
        <div className="space-y-1 rounded-md border border-solid border-[#E9EAEC] bg-[#F8F8F8] p-3 text-[13px]">
          <p className="m-0">
            <b>Subject:</b> {result.draft.subject}
          </p>
          <pre className="m-0 whitespace-pre-wrap font-sans">
            <LinkedText text={result.draft.body} />
          </pre>
          {result.draft.warnings.length > 0 && <p className="m-0 text-xs text-[#B96716]">{result.draft.warnings.join(' · ')}</p>}
          {!result.profileRead && <p className="m-0 text-xs text-[#707070]">Written from the list card only — their full profile is not read yet.</p>}
        </div>
      )}
    </Section>
  );
}
